import { ChangeEvent, useEffect, useState } from 'react';

import * as yup from 'yup';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  createFilterOptions,
} from '@mui/material';

import { ICoSettlementAgentObj } from '../../../../../types/SettlementAgent';
import { DocGenerationSource } from '../../../../constants/docGenerationSource';
import {
  normalizePhoneInput,
  phoneObjToString,
} from '../../../../utils/helpers';
import { validationSchema } from '../../../../utils/validationSchemas';

export interface SettlementInputValues {
  id?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
}

interface ICoSaInviteItem {
  orderType: string;
  onUserChange: (event: Map<number, ICoSettlementAgentObj>) => void;
  getExistingSettlementAgentByEmail: (email: string) => any;
  coSaToPreview?: Map<number, ICoSettlementAgentObj>;
  setAllValid: (allValid: boolean) => void;
  existingSettlementAgentsOnTheOrder: string[];
  existingCoSa: any;
  maxCapacity?: number;
}

const filter = createFilterOptions<any>();

const isCoSaValid = (item: ICoSettlementAgentObj) => {
  const emailFilled = item.Email.trim() !== '';
  const firstNameFilled = item.FirstName.trim() !== '';
  const lastNameFilled = item.LastName.trim() !== '';
  const phoneNumberFilled = item.PhoneNumber.trim() !== '';

  const fieldsFilled =
    emailFilled && firstNameFilled && lastNameFilled && phoneNumberFilled;

  if (!item.ExistingCoSa) {
    const emailError = item.ErrorList?.EmailError !== null;
    const firstNameError = item.ErrorList?.FirstNameError !== null;
    const lastNameError = item.ErrorList?.LastNameError !== null;
    const phoneNumberError = item.ErrorList?.PhoneNumberError !== null;

    if (emailError || firstNameError || lastNameError || phoneNumberError) {
      return false;
    }
  }

  return fieldsFilled;
};

const hasDuplicates = (
  value: Map<number, ICoSettlementAgentObj>,
  currentValue: string,
  currentId: number
) => {
  return Array.from(value.values()).some(
    (x) =>
      x.Id !== currentId &&
      x.Email?.toLocaleLowerCase() === currentValue?.toLocaleLowerCase()
  );
};

const areAllValid = (
  coSettlementAgentMap: Map<number, ICoSettlementAgentObj>
) => {
  return Array.from(coSettlementAgentMap.values()).every((i) => i.Valid);
};

const InviteCoSaItem = (props: ICoSaInviteItem) => {
  const {
    onUserChange,
    orderType,
    setAllValid,
    getExistingSettlementAgentByEmail,
    existingCoSa,
    coSaToPreview,
    existingSettlementAgentsOnTheOrder,
  } = props;

  const [coSaMap, setCoSaMap] = useState<Map<number, ICoSettlementAgentObj>>(
    coSaToPreview ||
      new Map<number, ICoSettlementAgentObj>([
        [
          1,
          {
            Email: '',
            FirstName: '',
            LastName: '',
            PhoneNumber: '',
            Id: 1,
            Valid: false,
            showExtraField: false,
            ErrorList: {
              EmailError: null,
              PhoneNumberError: null,
              FirstNameError: null,
              LastNameError: null,
            },
          },
        ],
      ])
  );
  const [optionsToShow, setOptionsToShow] = useState<SettlementInputValues[]>(
    []
  );

  useEffect(() => {
    let validChanged = false;
    const updatedCoSaMap = new Map(coSaMap);
    updatedCoSaMap.forEach((item, key) => {
      const isValid = isCoSaValid(item);
      if (item.Valid !== isValid) {
        validChanged = true;
        const updatedItem = { ...item, Valid: isValid };
        updatedCoSaMap.set(key, updatedItem);
      }
    });
    const allItemsValid = areAllValid(updatedCoSaMap);
    setAllValid(allItemsValid);
    if (validChanged) {
      setCoSaMap(updatedCoSaMap);
    }
    onUserChange(updatedCoSaMap);
  }, [coSaMap]);

  const checkExistingSaInOrder = (email: string) => {
    let settlementExistsInTheOrder = false;
    existingSettlementAgentsOnTheOrder.forEach((item) => {
      if (item === email) {
        settlementExistsInTheOrder = true;
        if (settlementExistsInTheOrder) {
          setOptionsToShow([
            {
              email: item,
            },
          ]);
        }
      }
    });

    return settlementExistsInTheOrder;
  };

  useEffect(() => {
    const updatedCoSaMap = new Map(coSaMap);
    coSaMap.forEach((item) => {
      if (
        existingCoSa &&
        existingCoSa.Email &&
        item.Email === existingCoSa.Email
      ) {
        updatedCoSaMap.set(item.Id, {
          Email: existingCoSa.Email,
          FirstName: existingCoSa.FirstName || '',
          LastName: existingCoSa.LastName || '',
          PhoneNumber: phoneObjToString(existingCoSa.PhoneNumber) || '',
          Id: coSaMap.get(item.Id)?.Id || 1,
          ExistingCoSa: true,
          showExtraField: true,
          ErrorList: {
            EmailError: '',
          },
          Valid: true,
        });
      }
      setCoSaMap(updatedCoSaMap);
    });
  }, [existingCoSa]);

  const handleEmailChange = (newValue: any, item: ICoSettlementAgentObj) => {
    const updatedCoSaMap = new Map(coSaMap);

    const resetCoSaFields = (
      email: string,
      isValid = false,
      showExtraField = false,
      emailError: string | null = null
    ) => ({
      ...updatedCoSaMap.get(item.Id),
      Id: item.Id,
      Email: email,
      FirstName: '',
      LastName: '',
      PhoneNumber: '',
      showExtraField,
      ErrorList: {
        EmailError: emailError,
        FirstNameError: null,
        LastNameError: null,
        PhoneNumberError: null,
      },
      Valid: isValid,
    });

    if (newValue && newValue.inputValue) {
      const emailChanged = item.Email !== newValue.inputValue;

      if (emailChanged) {
        try {
          validationSchema.validateSyncAt('Email', {
            Email: newValue.inputValue,
          });
          const duplicateEmailError = hasDuplicates(
            updatedCoSaMap,
            newValue.inputValue,
            item.Id
          )
            ? 'Your list contains duplicate entries.'
            : null;
          const existingSaError = checkExistingSaInOrder(newValue.inputValue)
            ? 'This email already exists in the order.'
            : duplicateEmailError;

          if (!existingSaError) {
            const existingCoSa = getExistingSettlementAgentByEmail(
              newValue.inputValue
            );
            const isCoSaValid =
              !existingCoSa || existingCoSa.Email !== newValue.inputValue;

            updatedCoSaMap.set(item.Id, {
              ...updatedCoSaMap.get(item.Id),
              Id: item.Id,
              Email: newValue.inputValue,
              FirstName: existingCoSa?.FirstName || '',
              LastName: existingCoSa?.LastName || '',
              PhoneNumber: existingCoSa?.PhoneNumber || '',
              showExtraField: true,
              ErrorList: {
                ...updatedCoSaMap.get(item.Id)?.ErrorList,
                EmailError: null,
              },
              Valid: isCoSaValid,
            });
          } else {
            updatedCoSaMap.set(
              item.Id,
              resetCoSaFields(
                newValue.inputValue,
                false,
                false,
                existingSaError
              )
            );
          }
        } catch (error) {
          if (error instanceof yup.ValidationError) {
            updatedCoSaMap.set(
              item.Id,
              resetCoSaFields(newValue.inputValue, false, false, error.message)
            );
          }
        }
      }

      setCoSaMap(updatedCoSaMap);
    } else {
      updatedCoSaMap.set(item.Id, resetCoSaFields('', true));
      setCoSaMap(updatedCoSaMap);
    }
  };

  const handleFieldChange = (
    fieldType: 'FirstName' | 'LastName' | 'PhoneNumber',
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    item: ICoSettlementAgentObj
  ) => {
    const updatedCoSaMap = new Map(coSaMap);
    let updatedValue = event.target.value;

    if (fieldType === 'PhoneNumber') {
      updatedValue = normalizePhoneInput(updatedValue).trimStart();
    }

    try {
      validationSchema.validateSyncAt(fieldType, {
        [fieldType]: updatedValue,
      });
      updatedCoSaMap.set(item.Id, {
        ...item,
        [fieldType]: updatedValue,
        ErrorList: {
          ...item.ErrorList,
          [`${fieldType}Error`]: null,
        },
        Valid: true,
      });
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        updatedCoSaMap.set(item.Id, {
          ...item,
          [fieldType]: updatedValue,
          ErrorList: {
            ...item.ErrorList,
            [`${fieldType}Error`]: error.message,
          },
          Valid: false,
        });
      }
    }

    setCoSaMap(updatedCoSaMap);
    onUserChange(updatedCoSaMap);
  };

  const handleAddPanel = () => {
    const highestId = Math.max(...Array.from(coSaMap.keys()));
    const nextId = highestId + 1;
    const updatedCoSaMap = new Map(coSaMap);
    const lastItem = updatedCoSaMap.get(highestId);

    if (lastItem && lastItem.Email.trim() !== '') {
      updatedCoSaMap.set(nextId, {
        Email: '',
        FirstName: '',
        LastName: '',
        PhoneNumber: '',
        Id: nextId,
        ErrorList: {
          EmailError: null,
          PhoneNumberError: null,
          FirstNameError: null,
          LastNameError: null,
        },
        Valid: false,
      });
      setCoSaMap(updatedCoSaMap);
    }
  };

  const handleDeletePanel = (item: ICoSettlementAgentObj) => {
    const next = new Map(coSaMap);
    next.delete(item.Id);
    setCoSaMap(next);

    const checkAllValid = areAllValid(next);
    setAllValid(checkAllValid);
  };

  return (
    <div>
      <List>
        {coSaMap.size > 0 &&
          Array.from(coSaMap.values()).map((item, index) => (
            <ListItem
              key={index}
              secondaryAction={
                coSaMap.size > 1 && (
                  <IconButton
                    type='button'
                    aria-label='remove sa field'
                    edge='end'
                    onClick={() => {
                      handleDeletePanel(item);
                    }}>
                    <DeleteOutlineIcon />
                  </IconButton>
                )
              }>
              <Grid container spacing={2} wrap={'nowrap'} alignItems={'center'}>
                <Grid item xs={item.showExtraField ? 4 : 12}>
                  <Autocomplete
                    value={coSaMap.get(item.Id)?.Email as string}
                    disabled={false}
                    freeSolo
                    autoHighlight
                    autoComplete
                    data-testid='InviteCoSaItem'
                    id='InviteCoSaItem'
                    options={optionsToShow}
                    noOptionsText={'No option found.'}
                    getOptionLabel={(option: any) => {
                      let label: string;
                      if (typeof option === 'string') {
                        label = option;
                      } else if (option.inputValue) {
                        label = option.inputValue;
                      } else {
                        label = `${option.searchField}`;
                      }
                      return label;
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);
                      const { inputValue } = params;
                      const isExisting = options.some(
                        (option) =>
                          inputValue.toLocaleLowerCase() ===
                          option.email?.toLocaleLowerCase()
                      );
                      if (inputValue !== '' && !isExisting) {
                        filtered.push({
                          inputValue,
                          title: `Add "${inputValue}"`,
                        });
                      }
                      return filtered;
                    }}
                    onChange={(e: any, newValue: any) => {
                      handleEmailChange(newValue, item);
                    }}
                    renderOption={(props, option: any) => {
                      if (option.email) {
                        return (
                          <ListItem
                            {...props}
                            key={option.id}
                            data-testid={option.id}>
                            <ListItemText primary={<>{`${option.email} `}</>} />
                          </ListItem>
                        );
                      } else {
                        return (
                          <li key={option.id} {...props}>
                            {option.title}
                          </li>
                        );
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        required
                        {...params}
                        data-testid={'settlementSelectorSearch'}
                        variant='standard'
                        label={
                          orderType === DocGenerationSource.FA
                            ? 'Escrow Assistant Email'
                            : 'Co-Settlement Agent Email'
                        }
                        error={!!coSaMap.get(item.Id)?.ErrorList?.EmailError}
                        helperText={coSaMap.get(item.Id)?.ErrorList?.EmailError}
                        placeholder={
                          orderType === DocGenerationSource.FA
                            ? 'Search for or add a new escrow assistant'
                            : 'Search for or add a new Co-Settlement Agent'
                        }
                        InputProps={{
                          ...params.InputProps,
                        }}
                      />
                    )}
                  />
                </Grid>
                {item.showExtraField && (
                  <>
                    <Grid item xs={3}>
                      <TextField
                        id={`first-name-${index}`}
                        label='First Name'
                        variant='standard'
                        disabled={!!(item.ExistingCoSa && item.FirstName)}
                        required
                        value={item.FirstName}
                        onChange={(e) =>
                          handleFieldChange('FirstName', e, item)
                        }
                        error={
                          !!coSaMap.get(item.Id)?.ErrorList?.FirstNameError
                        }
                        helperText={
                          coSaMap.get(item.Id)?.ErrorList?.FirstNameError
                        }
                      />
                    </Grid>
                    <Grid item xs={3}>
                      <TextField
                        id={`last-name-${index}`}
                        label='Last Name'
                        variant='standard'
                        disabled={!!(item.ExistingCoSa && item.LastName)}
                        required
                        value={item.LastName}
                        onChange={(e) => handleFieldChange('LastName', e, item)}
                        error={!!coSaMap.get(item.Id)?.ErrorList?.LastNameError}
                        helperText={
                          coSaMap.get(item.Id)?.ErrorList?.LastNameError
                        }
                      />
                    </Grid>
                    <Grid item xs={2} flexGrow={1} flexShrink={0}>
                      <TextField
                        id={`phone-number-${index}`}
                        label='Phone Number'
                        variant='standard'
                        value={item.PhoneNumber}
                        required
                        disabled={!!(item.ExistingCoSa && item.PhoneNumber)}
                        onChange={(e) =>
                          handleFieldChange('PhoneNumber', e, item)
                        }
                        error={
                          !!coSaMap.get(item.Id)?.ErrorList?.PhoneNumberError
                        }
                        helperText={
                          coSaMap.get(item.Id)?.ErrorList?.PhoneNumberError
                        }
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </ListItem>
          ))}
      </List>
      <div>
        <Box sx={{ textAlign: 'right', py: 2 }}>
          <Button
            size='small'
            variant='text'
            disabled={false}
            onClick={handleAddPanel}>
            Add Another
          </Button>
        </Box>
      </div>
    </div>
  );
};
export default InviteCoSaItem;
